import { createApi } from '@reduxjs/toolkit/query/react';
import { PaylinkCustomerInfo } from 'skipify-types';
import {
  CreatePaylinkCustomerBody,
  SearchPaylinkCustomersQuery,
  UploadPaylinkCustomersResponse,
  AddConfigBody,
  ConfigResponse,
  DomainResponse,
  CallbackResponse,
  ApiKeyResponse,
  CreateApiKeyResponse,
  Country,
  ExternalCredentialType,
  GetCredentialsResponse,
  GetMerchantResponse,
  MerchantExternalCredential,
  MerchantAccess,
  BigCommerceOnboardingStepKey,
  CreateOnboardingBody,
  OnboardingSystemOfOrigin,
  GetMerchantsByUserQuery,
  GetPublicMerchantResponse,
  CreateChildMerchantBody,
  CreateChildMerchantResponse,
  CreateMerchantBody,
  CreateMerchantResponse,
  UpdateDefaultCredentialsBody,
  UpdateMerchantBody,
  AuditLogEntry,
  GetAuditLogsQuery,
  MerchantAgenticSettings,
  PatchAgenticSettingsArgs,
  PatchStoreReadinessArgs,
  PostAgenticSettingsArgs,
  PreviewAgenticVoiceArgs,
  AgenticVoiceProfilesResponse,
  DeleteAgenticPersonalityArgs,
  UploadAgenticAvatarArgs,
  AgenticAvatarUploadResponse,
  PortalUserDetail,
  PortalUserListItem,
  SearchPortalUsersQuery,
  MerchantPortalUsersQuery,
  CreatePortalUserBody,
  UpdatePortalUserBody,
  PortalUserAuditLogEntry,
  GetPortalUserAuditLogsQuery,
  AgentMetricsResponse,
  AgentWidgetMetricsResponse,
  GetAgentMetricsArgs,
  GetAgentWidgetMetricsArgs,
  MerchantOnboarding,
  PatchOnboardingFieldsBody,
  PatchOnboardingStepBody,
  ShopifyOnboardingStepKey,
} from './types';
import { PaginatedResponse } from '../types';
import { PaylinkCustomerInfoResponse } from '../paylink/types';
import { fetchBaseQueryWithAuth } from '../../fetchBaseQuery';
import { invalidateTagsOnSuccess, providesList, providesPaginatedList } from '../cacheHelpers';
import { runtimeConfig } from '../../../config/runtimeConfig';

const AGENT_ANALYTICS_CACHE_TTL_SECONDS = 120;

type UploadPaylinkCustomersRequest = {
  body: FormData;
  merchantId: string;
};

export const mmsApi = createApi({
  reducerPath: 'mmsApi',
  tagTypes: [
    'Merchant',
    'Customer',
    'Domain',
    'Config',
    'Callback',
    'ApiKeys',
    'Credential',
    'AuditLogs',
    'PortalUserAuditLogs',
    'AgenticSettings',
    'AgenticVoiceProfiles',
    'PortalUsers',
    'AgentMetrics',
    'AgentWidgetMetrics',
    'Onboarding',
  ],
  baseQuery: fetchBaseQueryWithAuth(runtimeConfig.mmsApiBaseUrl || ''),
  endpoints: (builder) => ({
    getMerchant: builder.query<GetMerchantResponse, string>({
      query: (merchantId) => ({
        url: `/merchants/${merchantId}`,
        method: 'GET',
        params: {
          // We need to get the merchant's directLineage
          include: ['configs'],
        },
      }),
      providesTags: (_result, _error, merchantId) => [{ type: 'Merchant', id: merchantId }],
    }),
    getAuditLogs: builder.query<PaginatedResponse<AuditLogEntry>, GetAuditLogsQuery>({
      query: ({ merchantId, orderBy = 'desc', pageSize = 25, pageNumber = 1, ...queryParams }) => ({
        url: `/merchants/${merchantId}/audit-logs`,
        method: 'GET',
        params: {
          orderBy,
          pageSize,
          pageNumber,
          ...queryParams,
        },
      }),
      serializeQueryArgs: (params) => params.queryArgs,
      providesTags: (result) => (result ? [{ type: 'AuditLogs', id: 'LIST' }] : []),
    }),
    updateMerchant: builder.mutation<
      GetMerchantResponse,
      { merchantId: string; body: UpdateMerchantBody }
    >({
      query: ({ merchantId, body }) => ({
        url: `/merchants/${merchantId}`,
        method: 'PATCH',
        body,
      }),
      // An ecommerceConnector also replaces the default ecommerce credential.
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'Merchant', id: merchantId },
        'Credential',
      ],
    }),
    deleteMerchant: builder.mutation<void, string>({
      query: (merchantId) => ({
        url: `/merchants/${merchantId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, merchantId) => [{ type: 'Merchant', id: merchantId }],
    }),
    createChildMerchant: builder.mutation<CreateChildMerchantResponse, CreateChildMerchantBody>({
      query: (body) => ({
        url: '/merchants',
        method: 'POST',
        body,
        params: { merchantId: body.partnerMerchantId },
      }),
    }),
    createMerchant: builder.mutation<CreateMerchantResponse, CreateMerchantBody>({
      query: (body) => ({
        url: '/merchants',
        method: 'POST',
        body,
      }),
    }),
    listOnboardings: builder.query<
      PaginatedResponse<MerchantOnboarding>,
      { pageNumber?: number; pageSize?: number; systemOfOrigin?: OnboardingSystemOfOrigin }
    >({
      query: ({ pageNumber = 1, pageSize = 25, systemOfOrigin = 'shopify' }) => ({
        url: '/onboardings',
        method: 'GET',
        params: { pageNumber, pageSize, systemOfOrigin },
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'Onboarding', id: 'LIST' },
              ...result.data.map((onboarding) => ({
                type: 'Onboarding' as const,
                id: onboarding.merchantId,
              })),
            ]
          : [{ type: 'Onboarding', id: 'LIST' }],
    }),
    getOnboarding: builder.query<
      MerchantOnboarding,
      { merchantId: string; systemOfOrigin?: OnboardingSystemOfOrigin }
    >({
      query: ({ merchantId, systemOfOrigin = 'shopify' }) => ({
        url: `/merchants/${merchantId}/onboardings/${systemOfOrigin}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { merchantId }) => [{ type: 'Onboarding', id: merchantId }],
    }),
    createOnboarding: builder.mutation<
      MerchantOnboarding,
      { merchantId: string; body: CreateOnboardingBody }
    >({
      query: ({ merchantId, body }) => ({
        url: `/merchants/${merchantId}/onboardings`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'Onboarding', id: 'LIST' },
        { type: 'Onboarding', id: merchantId },
        { type: 'Merchant', id: merchantId },
      ],
    }),
    patchOnboarding: builder.mutation<
      MerchantOnboarding,
      {
        merchantId: string;
        systemOfOrigin?: OnboardingSystemOfOrigin;
        body: PatchOnboardingFieldsBody;
      }
    >({
      query: ({ merchantId, systemOfOrigin = 'shopify', body }) => ({
        url: `/merchants/${merchantId}/onboardings/${systemOfOrigin}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'Onboarding', id: merchantId },
        { type: 'Onboarding', id: 'LIST' },
      ],
    }),
    patchOnboardingStep: builder.mutation<
      MerchantOnboarding,
      {
        merchantId: string;
        systemOfOrigin?: OnboardingSystemOfOrigin;
        key: ShopifyOnboardingStepKey | BigCommerceOnboardingStepKey;
        body: PatchOnboardingStepBody;
      }
    >({
      query: ({ merchantId, systemOfOrigin = 'shopify', key, body }) => ({
        url: `/merchants/${merchantId}/onboardings/${systemOfOrigin}/steps/${key}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'Onboarding', id: merchantId },
        { type: 'Onboarding', id: 'LIST' },
      ],
    }),
    deleteOnboarding: builder.mutation<
      MerchantOnboarding,
      { merchantId: string; systemOfOrigin?: OnboardingSystemOfOrigin }
    >({
      query: ({ merchantId, systemOfOrigin = 'shopify' }) => ({
        url: `/merchants/${merchantId}/onboardings/${systemOfOrigin}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'Onboarding', id: merchantId },
        { type: 'Onboarding', id: 'LIST' },
      ],
    }),
    getPublicMerchant: builder.query<GetPublicMerchantResponse, string>({
      query: (merchantId) => ({
        url: `/merchants/${merchantId}/public`,
        method: 'GET',
      }),
      providesTags: (_result, _error, merchantId) => [
        { type: 'Merchant', id: merchantId, public: true },
      ],
      extraOptions: { noAuth: true },
    }),
    getMerchantsByUser: builder.query<PaginatedResponse<MerchantAccess>, GetMerchantsByUserQuery>({
      query: ({ userId, token, params }) => ({
        url: `/merchant-accounts/${userId}/merchants`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params ?? {},
      }),
    }),
    updateDefaultCredentials: builder.mutation<
      GetMerchantResponse,
      { merchantId: string; externalCredentials: UpdateDefaultCredentialsBody }
    >({
      query: ({ merchantId, externalCredentials }) => ({
        url: `/merchants/${merchantId}/credentials`,
        method: 'PATCH',
        body: externalCredentials,
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'Merchant', id: merchantId },
        'Credential',
      ],
    }),
    createPaylinkCustomer: builder.mutation<PaylinkCustomerInfoResponse, CreatePaylinkCustomerBody>(
      {
        query: (body) => ({
          url: '/paylink-customers',
          method: 'POST',
          body,
        }),
        invalidatesTags: invalidateTagsOnSuccess(['Customer']),
      },
    ),
    searchPaylinkCustomers: builder.query<
      PaginatedResponse<PaylinkCustomerInfoResponse>,
      SearchPaylinkCustomersQuery
    >({
      query: ({ merchantId, ...queryParams }) => ({
        url: `/merchants/${merchantId}/paylink-customers`,
        method: 'GET',
        params: queryParams,
      }),
      serializeQueryArgs: (params) => params.queryArgs,
      providesTags: (result) => providesPaginatedList(result, 'Customer'),
    }),
    updatePaylinkCustomer: builder.mutation<
      PaylinkCustomerInfoResponse,
      Partial<PaylinkCustomerInfo> & { customerId: string }
    >({
      query: ({ customerId, ...body }) => ({
        url: `/paylink-customers/${customerId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateTagsOnSuccess((result) => [
        { type: 'Customer', id: result.payLinkCustomerId },
      ]),
    }),
    deletePaylinkCustomer: builder.mutation<void, string>({
      query: (customerId) => ({
        url: `/paylink-customers/${customerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateTagsOnSuccess((_result, _error, payLinkCustomerId) => [
        { type: 'Customer', id: payLinkCustomerId },
      ]),
    }),
    uploadPaylinkCustomers: builder.mutation<
      UploadPaylinkCustomersResponse,
      UploadPaylinkCustomersRequest
    >({
      query: ({ merchantId, body }) => ({
        url: '/paylink-customers/bulk-upload',
        method: 'POST',
        body,
        responseHandler: async (response) => {
          return {
            file: await response.text(),
            totalUploaded: parseInt(response.headers.get('X-Total-Uploaded')!, 10),
            totalErrors: parseInt(response.headers.get('X-Total-Errors')!, 10),
            totalDuplicates: parseInt(response.headers.get('X-Total-Duplicates')!, 10),
          };
        },
        params: { merchantId },
      }),
      invalidatesTags: invalidateTagsOnSuccess(['Customer']),
    }),
    getAllCardOnFileCustomers: builder.query<PaylinkCustomerInfoResponse[], string>({
      queryFn: async (merchantId, _api, _extraOptions, baseQuery) => {
        const customers: PaylinkCustomerInfoResponse[] = [];
        let pageNumber = 1;
        let hasMore = true;

        while (hasMore) {
          // eslint-disable-next-line no-await-in-loop
          const response = await baseQuery({
            url: `/merchants/${merchantId}/paylink-customers`,
            method: 'GET',
            params: {
              pageSize: 500,
              pageNumber,
              orderBy: 'asc',
              sortBy: 'firstName',
              cardOnFile: true,
            },
          });
          if (response.error) {
            return { error: response.error };
          }
          const { data, metadata } =
            response.data as PaginatedResponse<PaylinkCustomerInfoResponse>;

          customers.push(...data);
          hasMore = metadata.totalPages > pageNumber;
          pageNumber += 1;
        }

        return { data: customers };
      },
      providesTags: (result) => providesList(result, 'Customer', 'CARD_ON_FILE'),
    }),
    getConfigs: builder.query<Array<ConfigResponse>, string>({
      query: (merchantId) => ({
        url: `/merchants/${merchantId}/configs`,
        method: 'GET',
      }),
      providesTags: ['Config'],
    }),
    getAgenticSettings: builder.query<MerchantAgenticSettings, string>({
      query: (merchantId) => ({
        url: `/merchants/${merchantId}/agentic-settings`,
        method: 'GET',
      }),
      providesTags: (_result, _error, merchantId) => [{ type: 'AgenticSettings', id: merchantId }],
    }),
    getAgenticVoiceProfiles: builder.query<AgenticVoiceProfilesResponse, string>({
      query: (merchantId) => ({
        url: `/merchants/${merchantId}/agentic-settings/voice-profiles`,
        method: 'GET',
      }),
      providesTags: (_result, _error, merchantId) => [
        { type: 'AgenticVoiceProfiles', id: merchantId },
      ],
    }),
    patchAgenticSettings: builder.mutation<MerchantAgenticSettings, PatchAgenticSettingsArgs>({
      query: ({ merchantId, agentName, body }) => ({
        url: `/merchants/${merchantId}/agentic-settings/${agentName}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'AgenticSettings', id: merchantId },
      ],
    }),
    patchStoreReadiness: builder.mutation<MerchantAgenticSettings, PatchStoreReadinessArgs>({
      query: ({ merchantId, body }) => ({
        url: `/merchants/${merchantId}/agentic-settings`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'AgenticSettings', id: merchantId },
      ],
    }),
    postAgenticSettings: builder.mutation<MerchantAgenticSettings, PostAgenticSettingsArgs>({
      query: ({ merchantId, body }) => ({
        url: `/merchants/${merchantId}/agentic-settings`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'AgenticSettings', id: merchantId },
      ],
    }),
    previewAgenticVoice: builder.mutation<Blob, PreviewAgenticVoiceArgs>({
      query: ({ merchantId, agentName, body }) => ({
        url: `/merchants/${merchantId}/agentic-settings/${agentName}/voice-preview`,
        method: 'POST',
        body,
        responseHandler: async (response) => response.blob(),
      }),
    }),
    deleteAgenticPersonality: builder.mutation<void, DeleteAgenticPersonalityArgs>({
      query: ({ merchantId, agentName, personalityId }) => ({
        url: `/merchants/${merchantId}/agentic-settings/${agentName}/personalities/${personalityId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'AgenticSettings', id: merchantId },
      ],
    }),
    uploadAgenticAvatar: builder.mutation<AgenticAvatarUploadResponse, UploadAgenticAvatarArgs>({
      query: ({ merchantId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/merchants/${merchantId}/agentic-settings/avatars`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
    }),
    getAgentMetrics: builder.query<AgentMetricsResponse, GetAgentMetricsArgs>({
      query: ({ merchantId, agentName, from, to, excludeTestSessions, personalityIds }) => ({
        url: `/merchants/${merchantId}/agents/${agentName}/metrics`,
        method: 'GET',
        params: {
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(excludeTestSessions ? { excludeTestSessions: true } : {}),
          ...(personalityIds?.length ? { personalityIds: personalityIds.join(',') } : {}),
        },
      }),
      providesTags: (_result, _error, { merchantId, agentName }) => [
        { type: 'AgentMetrics', id: `${merchantId}-${agentName}` },
      ],
      keepUnusedDataFor: AGENT_ANALYTICS_CACHE_TTL_SECONDS,
    }),
    getAgentWidgetMetrics: builder.query<AgentWidgetMetricsResponse, GetAgentWidgetMetricsArgs>({
      query: ({ merchantId, from, to, excludeTestSessions }) => ({
        url: `/merchants/${merchantId}/metrics/agent-widget`,
        method: 'GET',
        params: {
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(excludeTestSessions ? { excludeTestSessions: true } : {}),
        },
      }),
      providesTags: (_result, _error, { merchantId }) => [
        { type: 'AgentWidgetMetrics', id: merchantId },
      ],
      keepUnusedDataFor: AGENT_ANALYTICS_CACHE_TTL_SECONDS,
    }),
    addConfig: builder.mutation<ConfigResponse, AddConfigBody>({
      query: ({ merchantId, ...body }) => ({
        url: `/merchants/${merchantId}/configs`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        'Config',
        { type: 'Merchant', id: merchantId, public: true },
      ],
    }),
    updateConfig: builder.mutation<ConfigResponse, Partial<ConfigResponse>>({
      query: ({ configId, merchantId, ...body }) => ({
        url: `/merchants/${merchantId}/configs/${configId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        'Config',
        { type: 'Merchant', id: merchantId, public: true },
      ],
    }),
    deleteConfig: builder.mutation<void, { merchantId: string; configId: string }>({
      query: ({ merchantId, configId }) => ({
        url: `/merchants/${merchantId}/configs/${configId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        'Config',
        { type: 'Merchant', id: merchantId, public: true },
      ],
    }),
    getDomains: builder.query<Array<DomainResponse>, string>({
      query: (merchantId) => ({
        url: `/merchants/${merchantId}/merchant-urls`,
        method: 'GET',
      }),
      serializeQueryArgs: (params) => params.queryArgs,
      providesTags: ['Domain'],
    }),
    createDomain: builder.mutation<DomainResponse, { merchantId: string; url: string }>({
      query: ({ merchantId, url }) => ({
        url: `/merchant-urls`,
        method: 'POST',
        body: { merchantId, url, backendUrl: url },
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        'Domain',
        { type: 'Merchant', id: merchantId, public: true },
      ],
    }),
    deleteDomain: builder.mutation<void, { merchantId: string; urlId: string }>({
      query: ({ urlId, merchantId }) => ({
        url: `/merchant-urls/${urlId}?merchantId=${merchantId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        'Domain',
        { type: 'Merchant', id: merchantId, public: true },
      ],
    }),
    updateDomain: builder.mutation<
      DomainResponse,
      { urlId: string; url: string; merchantId: string }
    >({
      query: ({ urlId, url, merchantId }) => ({
        url: `/merchant-urls/${urlId}`,
        method: 'PATCH',
        body: { url, backendUrl: url, merchantId },
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [
        'Domain',
        { type: 'Merchant', id: merchantId, public: true },
      ],
    }),
    getCallbacks: builder.query<CallbackResponse[], string>({
      query: (merchantId) => ({
        url: `/merchants/${merchantId}/callbacks`,
        method: 'GET',
      }),
      providesTags: ['Callback'],
    }),
    createCallback: builder.mutation<
      CallbackResponse,
      Pick<CallbackResponse, 'merchantId' | 'callbackType' | 'url' | 'headers'>
    >({
      query: ({ merchantId, url, callbackType, headers }) => {
        const body: any = { merchantId, url, callbackType };

        if (headers && Object.keys(headers).length > 0) {
          body.headers = headers;
        }

        return {
          url: `/merchants/${merchantId}/callbacks`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Callback'],
    }),
    updateCallback: builder.mutation<
      CallbackResponse,
      Partial<Pick<CallbackResponse, 'callbackType' | 'url' | 'headers'>> & {
        callbackId: string;
        merchantId: string;
      }
    >({
      query: ({ callbackId, merchantId, ...body }) => ({
        url: `/merchants/${merchantId}/callbacks/${callbackId}`,
        method: 'PATCH',
        body: {
          ...body,
          merchantId,
        },
      }),
      invalidatesTags: ['Callback'],
    }),
    deleteCallback: builder.mutation<
      void,
      {
        callbackId: string;
        merchantId: string;
      }
    >({
      query: ({ merchantId, callbackId }) => ({
        url: `/merchants/${merchantId}/callbacks/${callbackId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Callback'],
    }),
    getSupportedCountries: builder.query<Country[], void>({
      query: () => ({
        url: '/countries',
        method: 'GET',
      }),
    }),
    getApiKeys: builder.query<Array<ApiKeyResponse>, string>({
      query: (merchantId) => ({
        url: `/merchants/${merchantId}/api-keys`,
        method: 'GET',
      }),
      providesTags: ['ApiKeys'],
    }),
    createApiKey: builder.mutation<CreateApiKeyResponse, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `/merchants/${merchantId}/api-keys`,
        method: 'POST',
      }),
      invalidatesTags: ['ApiKeys'],
    }),
    deleteApiKey: builder.mutation<void, { apiKeyId: string; merchantId: string }>({
      query: ({ merchantId, apiKeyId }) => ({
        url: `/merchants/${merchantId}/api-keys/${apiKeyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ApiKeys'],
    }),
    getCredentials: builder.query<
      GetCredentialsResponse,
      { merchantId: string; type: ExternalCredentialType[] }
    >({
      query: ({ merchantId, type }) => {
        const query = type.map((t) => `type=${t}`).join('&');
        return {
          url: `/merchants/${merchantId}/credentials?${query}`,
          method: 'GET',
        };
      },
      providesTags: (_1, _2, { type }) => type.map((t) => ({ type: 'Credential', id: t })),
    }),
    createCredential: builder.mutation<
      MerchantExternalCredential,
      Omit<MerchantExternalCredential, 'credentialId'>
    >({
      query: (body) => ({
        url: `/merchants/${body.merchantId}/credentials`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_1, _2, { credentialType }) => [{ type: 'Credential', id: credentialType }],
    }),
    uploadLogo: builder.mutation<
      { logoSrc: string },
      { merchantId: string; file: File; type?: 'branding' | 'co-branding' }
    >({
      query: ({ merchantId, file, type }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/merchants/${merchantId}/configs/logos`,
          method: 'POST',
          body: formData,
          params: { type },
          formData: true,
        };
      },
      invalidatesTags: ['Config'],
    }),
    registerCtp: builder.mutation<void, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: '/partners/ctp-registration',
        method: 'POST',
        body: { merchantId },
      }),
      invalidatesTags: ['Config'],
    }),
    searchPortalUsers: builder.query<PaginatedResponse<PortalUserListItem>, SearchPortalUsersQuery>(
      {
        query: ({ pageNumber = 1, pageSize = 25, ...params }) => ({
          url: '/merchant-accounts',
          method: 'GET',
          params: {
            pageNumber,
            pageSize,
            ...params,
          },
        }),
        providesTags: (result) =>
          result
            ? [
                { type: 'PortalUsers', id: 'LIST' },
                ...result.data.map((user) => ({ type: 'PortalUsers' as const, id: user.userId })),
              ]
            : [{ type: 'PortalUsers', id: 'LIST' }],
      },
    ),
    getPortalUser: builder.query<PortalUserDetail, string>({
      query: (userId) => ({
        url: `/merchant-accounts/${encodeURIComponent(userId)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, userId) => [{ type: 'PortalUsers', id: userId }],
    }),
    createPortalUser: builder.mutation<PortalUserDetail, CreatePortalUserBody>({
      query: (body) => ({
        url: '/merchant-accounts',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'PortalUsers', id: 'LIST' },
        { type: 'PortalUserAuditLogs', id: 'LIST' },
      ],
    }),
    updatePortalUser: builder.mutation<
      PortalUserDetail,
      { userId: string; body: UpdatePortalUserBody }
    >({
      query: ({ userId, body }) => ({
        url: `/merchant-accounts/${encodeURIComponent(userId)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { userId, body }) => {
        const merchantTags =
          body.merchantIds?.map((merchantId) => ({
            type: 'PortalUsers' as const,
            id: `MERCHANT_${merchantId}`,
          })) ?? [];
        return [
          { type: 'PortalUsers', id: 'LIST' },
          { type: 'PortalUsers', id: userId },
          { type: 'PortalUserAuditLogs', id: 'LIST' },
          { type: 'PortalUserAuditLogs', id: userId },
          ...merchantTags,
        ];
      },
    }),
    deletePortalUser: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/merchant-accounts/${encodeURIComponent(userId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, userId) => [
        { type: 'PortalUsers', id: 'LIST' },
        { type: 'PortalUserAuditLogs', id: 'LIST' },
        { type: 'PortalUserAuditLogs', id: userId },
      ],
    }),
    resendPortalUserAccessEmail: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/merchant-accounts/${encodeURIComponent(userId)}/resend-activation-email`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, userId) => [
        { type: 'PortalUserAuditLogs', id: 'LIST' },
        { type: 'PortalUserAuditLogs', id: userId },
      ],
    }),
    listPortalUsersForMerchant: builder.query<
      PaginatedResponse<PortalUserListItem>,
      MerchantPortalUsersQuery
    >({
      query: ({ merchantId, pageNumber = 1, pageSize = 25, ...params }) => ({
        url: `/merchant-accounts/merchants/${encodeURIComponent(merchantId)}/users`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          ...params,
        },
      }),
      providesTags: (result, _error, { merchantId }) =>
        result
          ? [
              { type: 'PortalUsers', id: `MERCHANT_${merchantId}` },
              ...result.data.map((user) => ({ type: 'PortalUsers' as const, id: user.userId })),
            ]
          : [{ type: 'PortalUsers', id: `MERCHANT_${merchantId}` }],
    }),
    revokeMerchantAccess: builder.mutation<void, { userId: string; merchantId: string }>({
      query: ({ userId, merchantId }) => ({
        url: `/merchant-accounts/${encodeURIComponent(userId)}/merchants/${encodeURIComponent(merchantId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { merchantId, userId }) => [
        { type: 'PortalUsers', id: `MERCHANT_${merchantId}` },
        { type: 'PortalUsers', id: userId },
        { type: 'PortalUsers', id: 'LIST' },
        { type: 'PortalUserAuditLogs', id: 'LIST' },
        { type: 'PortalUserAuditLogs', id: userId },
      ],
    }),
    getPortalUserAuditLogs: builder.query<
      PaginatedResponse<PortalUserAuditLogEntry>,
      GetPortalUserAuditLogsQuery
    >({
      query: ({ orderBy = 'desc', pageSize = 25, pageNumber = 1, ...queryParams }) => ({
        url: '/merchant-accounts/audit-logs',
        method: 'GET',
        params: {
          orderBy,
          pageSize,
          pageNumber,
          ...queryParams,
        },
      }),
      serializeQueryArgs: (params) => params.queryArgs,
      providesTags: (result) =>
        result
          ? [
              { type: 'PortalUserAuditLogs', id: 'LIST' },
              ...result.data.map((entry) => ({
                type: 'PortalUserAuditLogs' as const,
                id: entry.targetUserId,
              })),
            ]
          : [{ type: 'PortalUserAuditLogs', id: 'LIST' }],
    }),
    getPortalUserAuditLogsByUser: builder.query<
      PaginatedResponse<PortalUserAuditLogEntry>,
      { userId: string } & Omit<GetPortalUserAuditLogsQuery, 'targetUserId'>
    >({
      query: ({ userId, orderBy = 'desc', pageSize = 25, pageNumber = 1, ...queryParams }) => ({
        url: `/merchant-accounts/${encodeURIComponent(userId)}/audit-logs`,
        method: 'GET',
        params: {
          orderBy,
          pageSize,
          pageNumber,
          ...queryParams,
        },
      }),
      serializeQueryArgs: (params) => params.queryArgs,
      providesTags: (_result, _error, { userId }) => [
        { type: 'PortalUserAuditLogs', id: userId },
        { type: 'PortalUserAuditLogs', id: 'LIST' },
      ],
    }),
  }),
});

export const getPublicMerchantInfo = (merchantId: string) =>
  fetch(`${runtimeConfig.mmsApiBaseUrl || ''}/merchants/${merchantId}/public`).then((res) =>
    res.json(),
  );

export const {
  useGetMerchantQuery,
  useLazyGetMerchantQuery,
  useGetAuditLogsQuery,
  useLazyGetAuditLogsQuery,
  useDeleteMerchantMutation,
  useUpdateMerchantMutation,
  useCreateChildMerchantMutation,
  useCreateMerchantMutation,
  useListOnboardingsQuery,
  useGetOnboardingQuery,
  useCreateOnboardingMutation,
  usePatchOnboardingMutation,
  usePatchOnboardingStepMutation,
  useDeleteOnboardingMutation,
  useLazyGetPublicMerchantQuery,
  useGetPublicMerchantQuery,
  useUpdateDefaultCredentialsMutation,
  useCreatePaylinkCustomerMutation,
  useLazyGetMerchantsByUserQuery,
  useLazySearchPaylinkCustomersQuery,
  useSearchPaylinkCustomersQuery,
  useUpdatePaylinkCustomerMutation,
  useDeletePaylinkCustomerMutation,
  useUploadPaylinkCustomersMutation,
  useGetAllCardOnFileCustomersQuery,
  useAddConfigMutation,
  useUpdateConfigMutation,
  useDeleteConfigMutation,
  useGetDomainsQuery,
  useCreateDomainMutation,
  useDeleteDomainMutation,
  useUpdateDomainMutation,
  useGetConfigsQuery,
  useGetAgenticSettingsQuery,
  useGetAgenticVoiceProfilesQuery,
  usePatchAgenticSettingsMutation,
  usePatchStoreReadinessMutation,
  usePostAgenticSettingsMutation,
  usePreviewAgenticVoiceMutation,
  useDeleteAgenticPersonalityMutation,
  useUploadAgenticAvatarMutation,
  useGetAgentMetricsQuery,
  useGetAgentWidgetMetricsQuery,
  useGetCallbacksQuery,
  useCreateCallbackMutation,
  useDeleteCallbackMutation,
  useUpdateCallbackMutation,
  useGetSupportedCountriesQuery,
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
  useGetCredentialsQuery,
  useCreateCredentialMutation,
  useUploadLogoMutation,
  useRegisterCtpMutation,
  useSearchPortalUsersQuery,
  useLazySearchPortalUsersQuery,
  useGetPortalUserQuery,
  useLazyGetPortalUserQuery,
  useCreatePortalUserMutation,
  useUpdatePortalUserMutation,
  useDeletePortalUserMutation,
  useResendPortalUserAccessEmailMutation,
  useListPortalUsersForMerchantQuery,
  useRevokeMerchantAccessMutation,
  useGetPortalUserAuditLogsQuery,
  useLazyGetPortalUserAuditLogsQuery,
  useGetPortalUserAuditLogsByUserQuery,
  useLazyGetPortalUserAuditLogsByUserQuery,
} = mmsApi;
